import {Input, Text} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import {convertEdgeToUpsert} from 'analysis-tool/data-layout/LayoutViewUtils';
import AnalysisViewSelector from 'analysis-tool/ui/components/AnalysisViewSelector';
import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import useStepTypeInfos from 'pipelines/hooks/useStepTypeInfosQuery';
import {PipelineEdgeUpsertById} from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL, PipelineStep, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {resolveOutputStepData} from 'pipelines/PipelineViewUtils';
import {getUserFacingStepTypeName} from 'pipelines/StepTypeInfoUtils';

import {useStepConfigEditorContext} from '../../StepConfigEditorContext';
import {CustomComponentEditorProps} from '../../StepConfigEditorTypes';

interface SingleInputSourceSelectorProps extends CustomComponentEditorProps {
  inputStepDataName: string;
  label?: string;
  dataKind?: PipelineStepDataKind;
}

function getEdgesExcludingInputDataset(
  pipeline: Pick<PipelineGraphQL, 'id' | 'steps' | 'views'>,
  pipelineStepId: string,
  inputStepDataName: string
): Array<PipelineEdgeUpsertById> {
  const currentStep = pipeline.steps.find((s) => s.id === pipelineStepId);
  if (currentStep == null) {
    return [];
  }
  return currentStep.incomingEdges
    .filter((e) => e.toStepData.name !== inputStepDataName)
    .map((e) => convertEdgeToUpsert(e));
}

function getInputEdge(
  pipeline: Pick<PipelineGraphQL, 'id' | 'steps' | 'views'>,
  pipelineStepId: string,
  inputStepDataName: string
): PipelineStep['incomingEdges'][number] | null {
  const currentStep = pipeline.steps.find((s) => s.id === pipelineStepId);
  if (currentStep == null) {
    return null;
  }
  return currentStep.incomingEdges.find((e) => e.toStepData.name === inputStepDataName) ?? null;
}

function lineageNodeMatchesEdge(
  node: PipelineViewGraphQL['lineage'][number],
  inputEdge: PipelineStep['incomingEdges'][number]
): boolean {
  if (node.stepOutputReference == null) {
    return false;
  }
  return (
    node.step.id === inputEdge.fromStep.id &&
    node.stepOutputReference.outputStepData.id === inputEdge.fromStepData.id
  );
}

/**
 * Resolve the view connected to the given input, matching the edge source against a view's lineage.
 *
 * A view whose terminal lineage node matches is preferred; if none match terminally, a view where
 * any intermediate lineage node matches is returned.
 */
function getConnectedView(
  pipeline: Pick<PipelineGraphQL, 'id' | 'steps' | 'views'>,
  pipelineStepId: string,
  inputStepDataName: string
): PipelineViewGraphQL | null {
  const inputEdge = getInputEdge(pipeline, pipelineStepId, inputStepDataName);
  if (inputEdge == null) {
    return null;
  }
  const terminalMatch = pipeline.views.find((view) => {
    const lastNode = view.lineage[view.lineage.length - 1];
    return lastNode != null && lineageNodeMatchesEdge(lastNode, inputEdge);
  });
  if (terminalMatch != null) {
    return terminalMatch;
  }
  return (
    pipeline.views.find((view) => view.lineage.some((node) => lineageNodeMatchesEdge(node, inputEdge))) ??
    null
  );
}

/**
 * Resolve the source step feeding the given input, used to render a fallback label when the edge
 * source does not correspond to any view's lineage node.
 */
function getConnectedSourceStep(
  pipeline: Pick<PipelineGraphQL, 'id' | 'steps' | 'views'>,
  pipelineStepId: string,
  inputStepDataName: string
): PipelineStep | null {
  const inputEdge = getInputEdge(pipeline, pipelineStepId, inputStepDataName);
  if (inputEdge == null) {
    return null;
  }
  return pipeline.steps.find((s) => s.id === inputEdge.fromStep.id) ?? null;
}

export default function SingleInputSourceSelectorEditor({
  disabled,
  inputStepDataName,
  label,
  dataKind = PipelineStepDataKind.DATASET,
}: SingleInputSourceSelectorProps): JSX.Element {
  const {pipeline, pipelineStepId, onChangeIncomingEdges} = useStepConfigEditorContext();
  const stepTypeInfos = useStepTypeInfos();

  const connectedView = useMemo(
    () =>
      pipeline != null && pipelineStepId != null
        ? getConnectedView(pipeline, pipelineStepId, inputStepDataName)
        : null,
    [pipeline, pipelineStepId, inputStepDataName]
  );
  const connectedSourceStep = useMemo(
    () =>
      pipeline != null && pipelineStepId != null && connectedView == null
        ? getConnectedSourceStep(pipeline, pipelineStepId, inputStepDataName)
        : null,
    [pipeline, pipelineStepId, inputStepDataName, connectedView]
  );
  const [draftSelectedView, setDraftSelectedView] = useState<PipelineViewGraphQL | null | undefined>(
    undefined
  );
  const selectedView = draftSelectedView !== undefined ? draftSelectedView : connectedView;

  const handleSelect = (view: PipelineViewGraphQL | null): void => {
    if (disabled || pipeline == null || pipelineStepId == null || onChangeIncomingEdges == null) {
      return;
    }
    const existingEdges = getEdgesExcludingInputDataset(pipeline, pipelineStepId, inputStepDataName);
    if (view == null) {
      onChangeIncomingEdges(existingEdges);
      setDraftSelectedView(null);
      return;
    }
    const lastNode = view.lineage[view.lineage.length - 1];
    if (lastNode?.stepOutputReference == null) {
      return;
    }
    const resolvedStepData = resolveOutputStepData(pipeline.steps, {
      step: lastNode.step,
      stepOutputReference: lastNode.stepOutputReference,
    });

    const newEdge: PipelineEdgeUpsertById = {
      fromStepId: lastNode.step.id,
      fromStepDataName: resolvedStepData.name,
      fromStepDataIndex: null,
      toStepDataName: inputStepDataName,
      toStepDataIndex: null,
      condition: null,
    };

    onChangeIncomingEdges([...existingEdges, newEdge]);
    setDraftSelectedView(view);
  };

  if (pipeline == null || pipelineStepId == null) {
    return <></>;
  }

  const showSourceStepFallback = draftSelectedView === undefined && connectedSourceStep != null;

  return (
    <div>
      {label != null && <Input.Label>{label}</Input.Label>}
      {showSourceStepFallback && (
        <Text size="sm" color="dimmed">
          Connected to: {getUserFacingStepTypeName(connectedSourceStep.stepType, stepTypeInfos)}
        </Text>
      )}
      <AnalysisViewSelector
        analysis={pipeline as PipelineGraphQL}
        topLevelOnly={false}
        dataKind={dataKind}
        selectedView={selectedView}
        onSelect={handleSelect}
        disabled={disabled}
      />
    </div>
  );
}
